import { useState } from "react";
import { Link } from "react-router-dom";
import type { Lead } from "@sales-ops-amplify/shared";
import { LeadEditForm } from "@/components/companies/lead-panel";
import { ExternalLinksList } from "@/components/external-links-editor";

export function SavedPersonDrawer({
  person,
  onClose,
  onEnrich,
  onDelete,
  enriching,
  deleting,
}: {
  person: Lead;
  onClose: () => void;
  onEnrich: () => void;
  onDelete?: () => void;
  enriching?: boolean;
  deleting?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const location = [
    person.city ?? person.address?.city,
    person.state ?? person.address?.state,
    person.country ?? person.address?.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/30"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-full max-w-md flex-col border-l border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {person.name}
            </h2>
            <p className="text-sm text-muted-foreground">
              {[person.title, person.accountId].filter(Boolean).join(" · ") ||
                "Lead"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </header>

        {!editing ? (
          <>
            <div className="flex flex-wrap gap-2 border-b border-border px-5 py-3">
              {person.leadId ? (
                <Link
                  to={`/user/leads/${person.leadId}`}
                  className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40"
                >
                  Open profile
                </Link>
              ) : null}
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground"
              >
                Edit
              </button>
              <button
                type="button"
                disabled={enriching}
                onClick={onEnrich}
                className="rounded-md border border-border px-2.5 py-1.5 text-xs disabled:opacity-50"
              >
                Access email / phone
              </button>
              {person.accountId && (
                <Link
                  to={`/user/accounts/${person.accountId}`}
                  className="rounded-md border border-border px-2.5 py-1.5 text-xs"
                >
                  View company
                </Link>
              )}
              {onDelete && (
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete ${person.name}? This permanently removes your copy.`,
                      )
                    ) {
                      onDelete();
                    }
                  }}
                  className="rounded-md border border-destructive/40 px-2.5 py-1.5 text-xs text-destructive disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              )}
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
              <section className="space-y-1">
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  Person
                </p>
                <p>
                  Email:{" "}
                  {person.email ? (
                    <a
                      href={`mailto:${person.email}`}
                      className="text-brand hover:underline"
                    >
                      {person.email}
                    </a>
                  ) : (
                    "—"
                  )}
                </p>
                <p>Phone: {person.phone ?? person.mobilePhone ?? "—"}</p>
                <p>Location: {location || "—"}</p>
                <p>Seniority: {person.seniority ?? "—"}</p>
                <p>
                  Department:{" "}
                  {person.departments?.length
                    ? person.departments.join(", ")
                    : "—"}
                </p>
                {person.linkedinUrl && (
                  <a
                    href={person.linkedinUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand hover:underline"
                  >
                    LinkedIn profile
                  </a>
                )}
              </section>
              <section className="space-y-1">
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  CRM
                </p>
                <p className="capitalize">
                  Stage: {person.lifecycleStage ?? "—"}
                </p>
                <p className="capitalize">Source: {person.source ?? "—"}</p>
                <p className="capitalize">
                  Relationship: {person.relationship ?? "—"}
                </p>
                {person.notes && <p>Notes: {person.notes}</p>}
              </section>
              <section className="space-y-1">
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  External links
                </p>
                <ExternalLinksList links={person.externalLinks} />
              </section>
              {!!person.opportunityIds?.length && (
                <section className="space-y-1">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">
                    Deals
                  </p>
                  {person.opportunityIds.map((oid) => (
                    <Link
                      key={oid}
                      to={`/deals/${oid}`}
                      className="block text-brand hover:underline"
                    >
                      {oid}
                    </Link>
                  ))}
                </section>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <LeadEditForm
              contact={person}
              onSaved={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          </div>
        )}
      </aside>
    </div>
  );
}

